import { normalizeLineEndings } from "@/lib/tool-kit/admissions/counts";

/**
 * The Academic CV Generator's model: sections, dated entries in reverse chronological
 * order, and publications the writer typed or pasted.
 *
 * ## What the tool does and does not do
 *
 * Publications are never fetched or reformatted. Each line is a finished reference the
 * writer formatted in their field's style (with the citation generator, or by hand); the
 * tool only orders the lines, numbers them if asked, bolds the writer's own name and
 * keeps `*italics*`. Its sole inference is the year, read from the line to sort by.
 *
 * ## Entry syntax
 *
 * One entry per line. A date may lead the line, set off with a pipe, a colon or just a
 * space ("2019–2023 | PhD, University of Lagos"; "2021: Research fellow"). A line that
 * starts with a dash, a bullet or indentation is a detail of the entry above it.
 */

export const CV_SECTIONS = [
  "interests",
  "education",
  "appointments",
  "publications",
  "chapters",
  "conferences",
  "grants",
  "awards",
  "teaching",
  "talks",
  "service",
] as const;
export type CvSectionId = (typeof CV_SECTIONS)[number];

/** How each section is laid out. */
export const CV_SECTION_KINDS: Record<CvSectionId, CvKind> = {
  interests: "paragraph",
  education: "dated",
  appointments: "dated",
  publications: "references",
  chapters: "references",
  conferences: "references",
  grants: "dated",
  awards: "dated",
  teaching: "dated",
  talks: "dated",
  service: "dated",
};

export type CvKind = "paragraph" | "dated" | "references";
export type CvNumbering = "none" | "numbered" | "reverse";

export interface CvEntry {
  /** The date as written, ranges normalised to an en dash. Empty if undated. */
  date: string;
  text: string;
  details: string[];
  /** A reference's number, when the section is numbered. */
  number?: number;
}

export interface CvSection {
  id: CvSectionId;
  heading: string;
  kind: CvKind;
  entries: CvEntry[];
}

export interface CvHeader {
  name: string;
  title: string;
  affiliation: string;
  contacts: string[];
}

export interface CvModel {
  header: CvHeader;
  sections: CvSection[];
}

export interface CvInput {
  name: string;
  title: string;
  affiliation: string;
  email: string;
  phone: string;
  website: string;
  orcid: string;
  address: string;
  sections: Record<CvSectionId, string>;
  sortNewestFirst: boolean;
  numbering: CvNumbering;
  /** The writer's name as it appears in their references, to be set in bold. */
  boldName: string;
}

export interface CvLabels {
  placeholderName: string;
  orcid: string;
  headings: Record<CvSectionId, string>;
}

const DETAIL_LINE = /^(?:\s{2,}|\t)\S|^\s*[-•*–]\s+\S/;
const DETAIL_MARK = /^\s*[-•*–]?\s*/;
const PIPE_DATE = /^([^|\t]{2,40}?)\s*(?:\||\t)\s*(\S.*)$/;
// Words that end a date range without a year, and words that mark a reference as not yet
// published. English first, then the other tool languages (es, de, fr, it, pt, nl, pl, ru,
// tr, id, vi, hi, ja, ko, zh, ar). Spaced scripts are matched as whole words (JavaScript's
// \b knows only ASCII); Japanese, Korean and Chinese are matched wherever they occur,
// since those scripts run words together.
const WORD_EDGE_BEFORE = "(?<![\\p{L}\\p{M}\\p{N}_])";
const WORD_EDGE_AFTER = "(?![\\p{L}\\p{M}\\p{N}_])";

function wordAlternation(spaced: string[], unspaced: string[]): string {
  const words = spaced.map((word) => word.replaceAll(" ", "\\s+")).join("|");
  const run = unspaced.map((word) => word.replaceAll(" ", "\\s*")).join("|");
  return `(?:${WORD_EDGE_BEFORE}(?:${words})${WORD_EDGE_AFTER}|${run})`;
}

const OPEN_ENDED_WORDS = wordAlternation(
  [
    "present",
    "current",
    "ongoing",
    "now",
    "presente",
    "actualidad",
    "actual",
    "en curso",
    "hoy",
    "heute",
    "gegenwart",
    "aktuell",
    "laufend",
    "aujourd['’]hui",
    "présent",
    "actuel",
    "en cours",
    "maintenant",
    "oggi",
    "attuale",
    "in corso",
    "atual",
    "atualidade",
    "hoje",
    "em andamento",
    "heden",
    "nu",
    "huidig",
    "lopend",
    "obecnie",
    "obecny",
    "teraz",
    "do dziś",
    "trwa",
    "настоящее время",
    "по настоящее время",
    "наст\\. время",
    "н\\.\\s*в\\.",
    "günümüz",
    "devam ediyor",
    "halen",
    "şimdi",
    "sekarang",
    "saat ini",
    "kini",
    "berjalan",
    "nay",
    "hiện tại",
    "hiện nay",
    "đến nay",
    "वर्तमान",
    "अब तक",
    "अभी",
    "حتى الآن",
    "الحاضر",
    "حاليا",
    "حالياً",
    "الحالي",
    "مستمر",
    "الآن",
  ],
  [
    "現在",
    "現職",
    "継続中",
    "在職中",
    "현재",
    "재직 중",
    "진행 중",
    "至今",
    "迄今",
    "目前",
    "现在",
    "在职",
  ]
);

const NOT_YET_PUBLISHED_WORDS = wordAlternation(
  [
    "forthcoming",
    "in press",
    "accepted",
    "under review",
    "submitted",
    "in preparation",
    "en prensa",
    "próximo",
    "aceptado",
    "en revisión",
    "enviado",
    "en preparación",
    "im druck",
    "in druck",
    "angenommen",
    "in begutachtung",
    "eingereicht",
    "in vorbereitung",
    "erscheint demnächst",
    "sous presse",
    "à paraître",
    "accepté",
    "en évaluation",
    "soumis",
    "en préparation",
    "in stampa",
    "accettato",
    "in revisione",
    "sottomesso",
    "inviato",
    "in preparazione",
    "no prelo",
    "aceito",
    "em revisão",
    "submetido",
    "em preparação",
    "in druk",
    "geaccepteerd",
    "in beoordeling",
    "ingediend",
    "in voorbereiding",
    "w druku",
    "przyjęty",
    "w recenzji",
    "złożony",
    "w przygotowaniu",
    "в печати",
    "принято к печати",
    "на рецензии",
    "подано",
    "в подготовке",
    "baskıda",
    "kabul edildi",
    "değerlendirmede",
    "gönderildi",
    "hazırlanıyor",
    "dalam proses cetak",
    "dalam cetak",
    "diterima",
    "dalam peninjauan",
    "diajukan",
    "dalam persiapan",
    "đang in",
    "sắp xuất bản",
    "đã chấp nhận",
    "đang phản biện",
    "đã nộp",
    "đang soạn thảo",
    "प्रेस में",
    "स्वीकृत",
    "समीक्षाधीन",
    "तैयारी में",
    "قيد النشر",
    "قيد الطباعة",
    "مقبول",
    "قيد المراجعة",
    "قيد الإعداد",
  ],
  [
    "印刷中",
    "近刊",
    "受理済み",
    "査読中",
    "投稿中",
    "準備中",
    "인쇄 중",
    "출판 예정",
    "게재 승인",
    "심사 중",
    "투고 중",
    "준비 중",
    "在印",
    "即将出版",
    "已接受",
    "审稿中",
    "已投稿",
    "撰写中",
    "待发表",
  ]
);

const LEADING_YEARS = new RegExp(
  `^((?:19|20)\\d{2}(?:\\s*[–—-]\\s*(?:(?:19|20)\\d{2}|${OPEN_ENDED_WORDS}))?)(?:\\s*[:|,.;]\\s*|\\s+)(\\S.*)$`,
  "iu"
);
const YEAR_RANGE_DASH = /((?:19|20)\d{2})\s*[-—]\s*/g;
const YEAR = /(?:19|20)\d{2}/g;
const OPEN_ENDED = new RegExp(OPEN_ENDED_WORDS, "iu");
const PAREN_YEAR = /\(((?:19|20)\d{2})[a-z]?\)/;
const BARE_YEAR = /\b((?:19|20)\d{2})[a-z]?\b/;
const NOT_YET_PUBLISHED = new RegExp(NOT_YET_PUBLISHED_WORDS, "iu");
const ORCID_URL = /^https?:\/\/orcid\.org\//i;
const SPACES = /[ \t]+/g;

function normalizeDate(date: string): string {
  return date.replace(YEAR_RANGE_DASH, "$1–").replace(SPACES, " ").trim();
}

function splitDate(line: string): { date: string; text: string } {
  const lead = LEADING_YEARS.exec(line);
  if (lead) {
    return { date: normalizeDate(lead[1]), text: lead[2].trim() };
  }
  const pipe = PIPE_DATE.exec(line);
  if (pipe) {
    return { date: normalizeDate(pipe[1]), text: pipe[2].trim() };
  }
  return { date: "", text: line.trim() };
}

/** Parse the lines of one dated section into entries with their details. */
export function parseDatedEntries(raw: string): CvEntry[] {
  const entries: CvEntry[] = [];
  for (const line of normalizeLineEndings(raw).split("\n")) {
    if (line.trim() === "") {
      continue;
    }
    const previous = entries.at(-1);
    if (previous && DETAIL_LINE.test(line)) {
      previous.details.push(line.replace(DETAIL_MARK, "").trim());
    } else {
      entries.push({ ...splitDate(line), details: [] });
    }
  }
  return entries;
}

/** Parse a reference list: every non-blank line is one finished reference. */
export function parseReferences(raw: string): CvEntry[] {
  return normalizeLineEndings(raw)
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "")
    .map((text) => ({ date: "", text, details: [] }));
}

/** The year range in a date: [start, end], with end Infinity for "present". */
export function dateYears(date: string): [number, number] | null {
  const years = (date.match(YEAR) ?? []).map(Number);
  if (years.length === 0) {
    return null;
  }
  const start = Math.min(...years);
  const end = OPEN_ENDED.test(date)
    ? Number.POSITIVE_INFINITY
    : Math.max(...years);
  return [start, end];
}

/**
 * The year a reference was published, for ordering. The year in parentheses wins
 * ("(2025)" in APA); otherwise the first four-digit year. Work that is forthcoming,
 * accepted or under review has no year yet and sorts first.
 */
export function referenceYear(text: string): number | null {
  if (NOT_YET_PUBLISHED.test(text)) {
    return Number.POSITIVE_INFINITY;
  }
  const match = PAREN_YEAR.exec(text) ?? BARE_YEAR.exec(text);
  return match ? Number(match[1]) : null;
}

type SortKey = [number, number];

function sortKey(entry: CvEntry, kind: CvKind): SortKey | null {
  if (kind === "references") {
    const year = referenceYear(entry.text);
    return year === null ? null : [year, year];
  }
  const years = dateYears(entry.date);
  return years ? [years[1], years[0]] : null;
}

/**
 * Newest first by end year, then by start year. Entries with no usable date keep their
 * place after the dated ones, in the order typed. Stable: equal keys stay as typed.
 */
export function sortNewestFirst(entries: CvEntry[], kind: CvKind): CvEntry[] {
  const keyed = entries.map((entry, index) => ({
    entry,
    index,
    key: sortKey(entry, kind),
  }));
  keyed.sort((a, b) => {
    if (a.key === null || b.key === null) {
      if (a.key === b.key) {
        return a.index - b.index;
      }
      return a.key === null ? 1 : -1;
    }
    return b.key[0] - a.key[0] || b.key[1] - a.key[1] || a.index - b.index;
  });
  return keyed.map((item) => item.entry);
}

/** Add reference numbers: 1 = newest, or counting down so the newest has the highest. */
export function numberEntries(
  entries: CvEntry[],
  numbering: CvNumbering
): CvEntry[] {
  if (numbering === "none") {
    return entries;
  }
  const total = entries.length;
  return entries.map((entry, index) => ({
    ...entry,
    number: numbering === "numbered" ? index + 1 : total - index,
  }));
}

function sectionEntries(
  id: CvSectionId,
  raw: string,
  input: CvInput
): CvEntry[] {
  const kind = CV_SECTION_KINDS[id];
  if (kind === "paragraph") {
    const text = normalizeLineEndings(raw).trim();
    return text === "" ? [] : [{ date: "", text, details: [] }];
  }
  const parsed =
    kind === "references" ? parseReferences(raw) : parseDatedEntries(raw);
  const ordered = input.sortNewestFirst
    ? sortNewestFirst(parsed, kind)
    : parsed;
  return kind === "references"
    ? numberEntries(ordered, input.numbering)
    : ordered;
}

/** ORCID as the header prints it: the bare iD, or the iD from a pasted link. */
export function orcidLine(raw: string, label: string): string {
  const id = raw.trim().replace(ORCID_URL, "");
  return id === "" ? "" : `${label} ${id}`;
}

/** The whole CV as a model every exporter draws from. */
export function buildCv(input: CvInput, labels: CvLabels): CvModel {
  const contacts = [
    input.email.trim(),
    input.phone.trim(),
    input.website.trim(),
    orcidLine(input.orcid, labels.orcid),
    input.address.trim(),
  ].filter((line) => line !== "");
  const sections: CvSection[] = [];
  for (const id of CV_SECTIONS) {
    const entries = sectionEntries(id, input.sections[id], input);
    if (entries.length > 0) {
      sections.push({
        id,
        heading: labels.headings[id],
        kind: CV_SECTION_KINDS[id],
        entries,
      });
    }
  }
  return {
    header: {
      name: input.name.trim() || labels.placeholderName,
      title: input.title.trim(),
      affiliation: input.affiliation.trim(),
      contacts,
    },
    sections,
  };
}
